/**
 * SolicitudesDocPage — Bandeja del colector.
 *
 * Muestra todas las solicitudes de documentación pendientes / en curso
 * abiertas para los colectores. Cada fila lleva acciones (Cogerla,
 * Entregar, Liberar, Cancelar). Lo de "Entregar" inline lo hace ya la
 * tarjeta `SolicitudesDocCard`, pero aquí enlazamos al expediente.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ClipboardText, ArrowsClockwise, ArrowRight, UserCircle, Users, Wrench,
  Clock, Funnel,
} from "@phosphor-icons/react";

const FILTERS = [
  { k: "abiertas", label: "Abiertas" },
  { k: "pendiente", label: "Pendientes" },
  { k: "en_curso", label: "En curso" },
  { k: "entregada", label: "Entregadas" },
  { k: "cancelada", label: "Canceladas" },
];

const STATUS_BADGE = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  en_curso:  { label: "En curso",  cls: "bg-blue-100 text-blue-800 border-blue-200" },
  entregada: { label: "Entregada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

const TARGET_ICON = {
  asegurado: UserCircle,
  perjudicado: Users,
  reparador: Wrench,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function SolicitudesDocPage({ embedded = false }) {
  const { user } = useAuth();
  const [filter, setFilter] = useState("abiertas");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const canSeeQueue = !!(user?.is_colector || user?.role === "admin");

  const load = useCallback(async () => {
    if (!canSeeQueue) { setItems([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/solicitudes-doc/my-queue?status=${filter}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [filter, canSeeQueue]);

  useEffect(() => { load(); }, [load]);

  const take = async (id) => {
    try {
      await api.post(`/solicitudes-doc/${id}/take`);
      toast.success("La has cogido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // Si el usuario no es colector mostramos un mensaje claro en lugar de
  // dejarle reventar contra el endpoint. `canSeeQueue` viene del useState arriba.

  return (
    <div className={embedded ? "" : "bg-background min-h-screen"} data-testid="solicitudes-doc-page">
      {!embedded && (
        <header className="sticky top-0 z-10 bg-white border-b border-border px-4 md:px-6 h-14 flex items-center gap-3">
          <ClipboardText className="w-5 h-5 text-brand" weight="duotone" />
          <h1 className="font-display font-bold text-base md:text-lg">Solicitudes de documentación</h1>
          <button
            onClick={load}
            className="ml-auto text-muted hover:text-foreground"
            title="Refrescar"
            data-testid="solicitudes-page-refresh"
          >
            <ArrowsClockwise className="w-4 h-4" weight="bold" />
          </button>
        </header>
      )}

      <div className={`${embedded ? "px-4 md:px-6 py-2" : "px-4 md:px-6 py-4"} max-w-6xl mx-auto space-y-4`}>
        {!canSeeQueue && (
          <div className="bg-white border border-border rounded-lg p-6 text-center">
            <Funnel className="w-6 h-6 text-muted mx-auto mb-2" weight="duotone" />
            <h2 className="font-display font-bold text-base">No eres colector</h2>
            <p className="text-[12.5px] text-muted mt-1">
              Para que aparezcan aquí solicitudes, un admin debe marcarte como{" "}
              <strong>"Recibe solicitudes"</strong> desde tu perfil.
            </p>
          </div>
        )}

        {canSeeQueue && (
          <>
            <div className="flex gap-1.5 flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  data-testid={`solicitudes-filter-${f.k}`}
                  className={`text-[12px] font-semibold px-3 py-1.5 rounded-md border transition-all ${
                    filter === f.k
                      ? "bg-foreground text-white border-foreground"
                      : "bg-white text-foreground border-border hover:border-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="bg-white border border-border rounded-lg overflow-hidden">
              {loading && items.length === 0 && (
                <div className="py-8 text-center text-muted text-sm">Cargando…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="py-10 text-center text-muted text-sm italic">
                  No hay solicitudes {filter !== "abiertas" ? `en estado "${filter}"` : "abiertas"} ahora mismo.
                </div>
              )}
              {items.length > 0 && (
                <ul className="divide-y divide-border" data-testid="solicitudes-page-list">
                  {items.map((s) => {
                    const Icon = TARGET_ICON[s.target_type] || ClipboardText;
                    const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pendiente;
                    const summary = s.expediente_summary || {};
                    return (
                      <li
                        key={s.id}
                        data-testid={`solicitudes-page-row-${s.id}`}
                        className="px-4 md:px-5 py-3 flex items-start gap-3 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-md bg-brand/10 border border-brand/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-brand" weight="duotone" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[13.5px] text-foreground">{s.target_label || s.target_type}</span>
                            <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border ${badge.cls}`}>
                              {badge.label}
                            </span>
                            {summary.numero_siniestro && (
                              <span className="text-[10.5px] font-mono text-muted">{summary.numero_siniestro}</span>
                            )}
                          </div>
                          <div className="text-[12px] text-foreground/90 mt-0.5 line-clamp-2 break-words">
                            {s.descripcion}
                          </div>
                          <div className="text-[10.5px] text-muted mt-1 inline-flex items-center gap-1.5 flex-wrap">
                            <Clock className="w-2.5 h-2.5" weight="bold" />
                            {fmtDate(s.created_at)}
                            {s.created_by_name && <span>· por {s.created_by_code || s.created_by_name}</span>}
                            {summary.asegurado_nombre && <span>· {summary.asegurado_nombre}</span>}
                            {summary.aseguradora && <span>· {summary.aseguradora}</span>}
                            {s.taken_by_name && <span>· tomada por <strong>{s.taken_by_name}</strong></span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          {s.status === "pendiente" && (
                            <button
                              onClick={() => take(s.id)}
                              className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-foreground text-white"
                              data-testid={`solicitudes-page-take-${s.id}`}
                            >
                              Cogerla
                            </button>
                          )}
                          <Link
                            to={`/review/${s.expediente_id}`}
                            data-testid={`solicitudes-page-goto-${s.id}`}
                            className="text-[11px] inline-flex items-center gap-1 text-brand hover:underline"
                          >
                            Ir al expediente <ArrowRight className="w-3 h-3" weight="bold" />
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
